import { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { useTranslation } from 'common/src/hooks/useTranslation';
import useUserStore from 'common/src/stores/useUserStore';
import { getChartThemeColors } from 'common/src/utils/chartTheme';
import { getIndustryVolumeArrivals, getIndustryVolumeArrivalsDetail } from 'common/src/api/industryVolume';
import { EmptyState, LoadingState } from '../ui';
import {
  arrivalBars,
  bucketEnd,
  bucketOf,
  bucketVoyages,
  destinationGroups,
  destinationKey,
  isoWeek,
  sundayOf,
  viaOptions,
  type ArrivalsData,
  type ColorBy,
  type Granularity,
  type Period,
  type Via,
} from '../../utils/industry-volume/build-arrivals';
import { displayName } from '../../utils/industry-volume/build-pulse';
import { day, dayRange, pct, thousands, week } from './format';
import SearchSelect from './SearchSelect';
import { useVolumeUnit } from './unit';

// Colores de los grupos apilados; el último ("otros") en gris.
const PALETTE = ['#7C3AED', '#10B981', '#F59E0B', '#3B82F6', '#EF4444', '#EC4899', '#14B8A6', '#84CC16', '#06B6D4'];
const OTHERS_COLOR = '#A1A1AA';
const VIA_LABELS: Record<Via, string> = { M: 'Marítimo', A: 'Aéreo', T: 'Terrestre' };
// Con la temporada en curso (dato de hace menos de 2 semanas), la tabla abre en
// la semana del dato; con la temporada cerrada, en la más cargada.
const OPEN_SEASON_DAYS = 14;
// Naves que se ven de entrada en la tabla; el resto, con "Ver todas".
const VOYAGES_SHOWN = 15;

const shortDay = (iso: string) => day(iso).split('-').slice(0, 2).join('-');
const monthLabel = (iso: string) => day(iso).split('-').slice(1).join('-');

type Basis = 'etd' | 'eta';

/**
 * Volumen de la temporada por fecha de arribo o de zarpe (según la semana de
 * referencia), por nave y puerto. Variedad y exportadora filtran en el
 * servidor; destino y flete, en el navegador.
 */
export default function ArrivalsCard({
  speciesId,
  season,
  basis,
}: {
  speciesId: number;
  season: string;
  basis: Basis;
}) {
  const { t } = useTranslation('volume');
  const [varieties, setVarieties] = useState<string[]>([]);
  const [exporterKeys, setExporterKeys] = useState<string[]>([]);
  const [data, setData] = useState<ArrivalsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getIndustryVolumeArrivals(speciesId, season, { varieties, exporterKeys }, basis)
      .then((result) => active && setData(result as ArrivalsData))
      .catch(() => active && setData(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [speciesId, season, varieties, exporterKeys, basis]);

  if (!data) {
    return loading ? <LoadingState label={t('Cargando naves…')} /> : null;
  }
  return (
    <ArrivalsView
      key={data.season}
      speciesId={speciesId}
      data={data}
      basis={(data.basis as Basis) ?? 'eta'}
      loading={loading}
      varieties={varieties}
      exporterKeys={exporterKeys}
      onVarietiesChange={setVarieties}
      onExporterKeysChange={setExporterKeys}
    />
  );
}

function ArrivalsView({
  speciesId,
  data,
  basis,
  loading,
  varieties,
  exporterKeys,
  onVarietiesChange,
  onExporterKeysChange,
}: {
  speciesId: number;
  data: ArrivalsData;
  basis: Basis;
  loading: boolean;
  varieties: string[];
  exporterKeys: string[];
  onVarietiesChange: (keys: string[]) => void;
  onExporterKeysChange: (keys: string[]) => void;
}) {
  const { t } = useTranslation('volume');
  const { darkMode } = useUserStore();
  const colors = getChartThemeColors(darkMode);
  const { v, toUnit, label, header } = useVolumeUnit();
  const u = t(label);
  const byDeparture = basis === 'etd';
  const [destinations, setDestinations] = useState<string[]>([]);
  const [vias, setVias] = useState<Via[]>([]);
  const [colorBy, setColorBy] = useState<ColorBy>('country');
  const [granularity, setGranularity] = useState<Granularity>('week');
  const [period, setPeriod] = useState<Period>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [showAllVoyages, setShowAllVoyages] = useState(false);

  const filters = { destinations, vias, period };
  const hasFilters = destinations.length + vias.length + varieties.length + exporterKeys.length > 0 || period !== 'all';
  const resetFilters = () => {
    setDestinations([]);
    setVias([]);
    setPeriod('all');
    setSelected(null);
    onVarietiesChange([]);
    onExporterKeysChange([]);
  };

  const destinationOptions = useMemo(
    () =>
      destinationGroups(data).map((c) => ({
        key: c.country,
        label: c.country,
        description: `${v(c.kilos)} ${u}`,
        items: c.ports.map((p) => ({
          key: destinationKey(c.country, p.port),
          label: p.port,
          description: `${v(p.kilos)} ${u}`,
        })),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, u]
  );
  const varietyOptions = (data.variety_options ?? []).map(([name, kilos]) => ({
    key: name,
    label: name,
    description: `${v(kilos)} ${u}`,
  }));
  const exporterOptions = (data.exporter_options ?? []).map(([key, name, kilos]) => ({
    key,
    label: displayName(name).toLocaleUpperCase('es'),
    description: `${v(kilos)} ${u}`,
  }));
  const freightOptions = viaOptions(data).map((o) => ({
    key: o.via,
    label: t(VIA_LABELS[o.via]),
    description: `${v(o.kilos)} ${u}`,
  }));
  const bars = arrivalBars(data, { ...filters, colorBy, granularity });

  const exporterNames = new Map(data.top_exporters.map((e) => [e.key, displayName(e.name).toLocaleUpperCase('es')]));
  const groupLabel = (name: string) => {
    if (name === 'OTRAS' || name === 'OTROS') return colorBy === 'country' ? t('Otros') : t('Otras');
    if (colorBy === 'via') return t(VIA_LABELS[name as Via] ?? name);
    return colorBy === 'exporter' ? (exporterNames.get(name) ?? name) : name;
  };
  const seriesColors = bars.series.map((s, i) =>
    s.name === 'OTRAS' || s.name === 'OTROS' ? OTHERS_COLOR : PALETTE[i % PALETTE.length]
  );
  const bucketLabel = (bucket: string) =>
    granularity === 'week'
      ? t('sem {{week}}', { week: week(isoWeek(bucket)) })
      : granularity === 'month'
        ? monthLabel(bucket)
        : shortDay(bucket);
  const cutBucket = data.data_date ? bucketOf(data.data_date, granularity) : null;
  // La tabla de naves siempre se ve: la barra elegida o la de por defecto.
  const seasonOpen =
    !!data.data_date && Date.now() - Date.parse(`${data.data_date}T00:00:00Z`) < OPEN_SEASON_DAYS * 864e5;
  const defaultBucket =
    seasonOpen && cutBucket && bars.buckets.includes(cutBucket) ? cutBucket : bars.busiest;
  const activeBucket = selected && bars.buckets.includes(selected) ? selected : defaultBucket;
  const activeIndex = activeBucket ? bars.buckets.indexOf(activeBucket) : -1;
  const voyages = activeBucket ? bucketVoyages(data, activeBucket, { ...filters, granularity }) : [];

  // Detalle de la barra según el desglose: con Flete, las naves (ya cargadas);
  // con los demás, la lista completa (sin "Otros") se pide al servidor.
  const dimension = colorBy === 'via' ? null : colorBy;
  const [detail, setDetail] = useState<[string, string, number, number][] | null>(null);
  const detailKey = [dimension, activeBucket, granularity, basis, destinations, vias, period, varieties, exporterKeys]
    .map((x) => (Array.isArray(x) ? x.join(',') : String(x)))
    .join('|');
  useEffect(() => {
    setDetail(null);
    if (!dimension || !activeBucket) return;
    let active = true;
    getIndustryVolumeArrivalsDetail(
      speciesId,
      data.season,
      basis,
      { from: activeBucket, to: bucketEnd(activeBucket, granularity) },
      dimension,
      { varieties, exporterKeys, destinations, vias, period }
    )
      .then((rows) => active && setDetail(rows))
      .catch(() => active && setDetail([]));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailKey, speciesId, data.season]);
  const detailTotal = (detail ?? []).reduce((sum, [, , kilos]) => sum + kilos, 0);

  // Con muchas barras (diario, o semanal en celular) se rotula una de cada N;
  // el tooltip muestra siempre la fecha completa. El corte siempre se rotula.
  const labels = bars.buckets.map(bucketLabel);
  const maxLabels = typeof window !== 'undefined' && window.innerWidth < 640 ? 8 : 24;
  const step = Math.max(1, Math.ceil(labels.length / maxLabels));
  const axisLabels = labels.map((l, i) => (i % step === 0 || bars.buckets[i] === cutBucket ? l : ''));
  const axisTitle = byDeparture
    ? granularity === 'day'
      ? t('Día de zarpe (ETD)')
      : granularity === 'month'
        ? t('Mes de zarpe (ETD)')
        : t('Semana de zarpe (ETD)')
    : granularity === 'day'
      ? t('Día de arribo (ETA)')
      : granularity === 'month'
        ? t('Mes de arribo (ETA)')
        : t('Semana de arribo (ETA)');

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      stacked: true,
      toolbar: { show: false },
      foreColor: colors.foreColor,
      background: 'transparent',
      events: {
        dataPointSelection: (_e, _ctx, config) => {
          setSelected(bars.buckets[config.dataPointIndex] ?? null);
          setShowAllVoyages(false);
        },
      },
    },
    theme: { mode: darkMode ? 'dark' : 'light' },
    // La barra elegida se ve resaltada: las demás, más claras.
    colors: seriesColors.map(
      (color) =>
        ({ dataPointIndex }: { dataPointIndex: number }) =>
          activeIndex === -1 || dataPointIndex === activeIndex ? color : `${color}55`
    ),
    states: { active: { filter: { type: 'none' } } },
    dataLabels: { enabled: false },
    grid: { borderColor: colors.gridBorderColor },
    legend: { show: false },
    xaxis: {
      categories: axisLabels,
      labels: { rotate: -45, hideOverlappingLabels: true },
      title: { text: axisTitle },
    },
    yaxis: { labels: { formatter: (value) => thousands(value) }, title: { text: u } },
    tooltip: {
      shared: true,
      intersect: false,
      x: { formatter: (_v, opts) => labels[opts?.dataPointIndex ?? 0] ?? '' },
      y: { formatter: (value) => `${thousands(value)} ${u}` },
    },
    annotations:
      cutBucket && bars.buckets.includes(cutBucket)
        ? {
            xaxis: [
              {
                x: bucketLabel(cutBucket),
                borderColor: '#7C3AED',
                strokeDashArray: 4,
                label: {
                  text: t('Corte · último zarpe informado {{date}}', { date: day(data.data_date) }),
                  style: { color: '#fff', background: '#7C3AED' },
                },
              },
            ],
          }
        : undefined,
  };

  // Período de la barra: "Semana 08 (16–22 feb)", "feb-2026" o "19-feb-2026".
  const periodLabel = !activeBucket
    ? ''
    : granularity === 'week'
      ? t('Semana {{week}} ({{range}})', {
          week: week(isoWeek(activeBucket)),
          range: dayRange(activeBucket, sundayOf(activeBucket)),
        })
      : granularity === 'month'
        ? monthLabel(activeBucket)
        : day(activeBucket);
  const DETAIL_TITLES = {
    exporter: { title: 'Exportadoras', one: '1 exportadora', many: '{{count}} exportadoras', column: 'EXPORTADORA' },
    variety: { title: 'Variedades', one: '1 variedad', many: '{{count}} variedades', column: 'VARIEDAD' },
    country: { title: 'Países de destino', one: '1 país', many: '{{count}} países', column: 'PAÍS' },
  } as const;
  const detailTitle = (() => {
    if (!activeBucket) return null;
    if (!dimension) {
      return {
        title: t('Naves y puertos de descarga'),
        count: t(voyages.length === 1 ? '1 nave' : '{{count}} naves', { count: voyages.length }),
        kilos: voyages.reduce((sum, x) => sum + x.kilos, 0),
      };
    }
    const texts = DETAIL_TITLES[dimension];
    const count = detail?.length ?? 0;
    return {
      title: t(texts.title),
      count: t(count === 1 ? texts.one : texts.many, { count }),
      kilos: detailTotal,
    };
  })();
  // Las más grandes; tu exportadora siempre se ve (al final si no está entre ellas).
  const detailShown = (() => {
    if (!detail) return [];
    if (showAllVoyages) return detail;
    const top = detail.slice(0, VOYAGES_SHOWN);
    const mine = dimension === 'exporter' ? detail.slice(VOYAGES_SHOWN).find((row) => row[3] > 0) : undefined;
    return mine ? [...top, mine] : top;
  })();

  return (
    <Card shadow='sm'>
      <CardBody className={`gap-4 p-6 transition-opacity ${loading ? 'opacity-50' : ''}`} aria-busy={loading}>
        <div>
          <h3 className='text-lg font-semibold'>
            {byDeparture ? t('Volumen por fecha de zarpe') : t('Volumen por fecha de arribo')}
          </h3>
          <p className='text-sm text-default-500'>
            {byDeparture
              ? t('Volumen de la industria según fecha de zarpe (ETD) de lo zarpado en la temporada, desglosado por destino, exportadora, variedad o flete. Selecciona una fecha en el gráfico para ver su detalle según el desglose activo.')
              : t('Volumen de la industria según fecha de arribo (ETA) de lo zarpado en la temporada, desglosado por destino, exportadora, variedad o flete. Selecciona una fecha en el gráfico para ver su detalle según el desglose activo.')}
          </p>
        </div>

        {/* Arriba las pestañas de vista; abajo, los filtros. */}
        <div className='flex flex-wrap items-center gap-3'>
          <Tabs
            size='sm'
            aria-label={t('Desglose')}
            selectedKey={colorBy}
            onSelectionChange={(k) => {
              setColorBy(k as ColorBy);
              setSelected(null);
              setShowAllVoyages(false);
            }}
          >
            <Tab key='country' title={t('Destino')} />
            <Tab key='variety' title={t('Variedad')} />
            <Tab key='exporter' title={t('Exportadora')} />
            <Tab key='via' title={t('Flete')} />
          </Tabs>
          <Tabs
            size='sm'
            aria-label={t('Agrupar')}
            selectedKey={granularity}
            onSelectionChange={(k) => {
              setGranularity(k as Granularity);
              setSelected(null);
            }}
          >
            <Tab key='day' title={t('Diario')} />
            <Tab key='week' title={t('Semanal')} />
            <Tab key='month' title={t('Mensual')} />
          </Tabs>
          <Tabs
            size='sm'
            aria-label={t('Período')}
            selectedKey={period}
            onSelectionChange={(k) => {
              setPeriod(k as Period);
              setSelected(null);
            }}
          >
            <Tab key='all' title={t('Toda la temporada')} />
            <Tab key='arrived' title={t('Arribado')} />
            <Tab key='pending' title={t('Por arribar')} />
          </Tabs>
        </div>
        <div className='flex flex-wrap items-end gap-3'>
          <SearchSelect
            multiple
            label={t('Destino')}
            allLabel={t('Todos los destinos ({{count}})', { count: destinationOptions.length })}
            groups={destinationOptions}
            selected={destinations}
            onChange={(keys) => {
              setDestinations(keys);
              setSelected(null);
            }}
          />
          <SearchSelect
            multiple
            label={t('Variedad')}
            allLabel={t('Todas las variedades')}
            options={varietyOptions}
            selected={varieties}
            onChange={(keys) => {
              onVarietiesChange(keys);
              setSelected(null);
            }}
          />
          <SearchSelect
            multiple
            label={t('Exportadora')}
            allLabel={t('Todas las exportadoras')}
            options={exporterOptions}
            selected={exporterKeys}
            onChange={(keys) => {
              onExporterKeysChange(keys);
              setSelected(null);
            }}
          />
          <SearchSelect
            multiple
            className='w-44'
            label={t('Flete')}
            allLabel={t('Todas las vías')}
            options={freightOptions}
            selected={vias}
            onChange={(keys) => {
              setVias(keys as Via[]);
              setSelected(null);
            }}
          />
          <Button
            size='sm'
            variant='light'
            startContent={<Icon icon='tabler:filter-off' />}
            isDisabled={!hasFilters}
            onPress={resetFilters}
          >
            {t('Resetear filtros')}
          </Button>
        </div>

        {bars.buckets.length ? (
          <>
            <div className='flex flex-wrap gap-2'>
              {bars.legend.map((l, i) => (
                <Chip
                  key={l.name}
                  size='sm'
                  variant='flat'
                  startContent={
                    <span className='ml-1 inline-block h-2 w-2 rounded-full' style={{ backgroundColor: seriesColors[i] }} />
                  }
                >
                  {groupLabel(l.name)} · {v(l.kilos)} {u} · {pct(l.share)}
                  {l.mine > 0 ? ` · ${t('tú {{kilos}}', { kilos: v(l.mine) })}` : ''}
                </Chip>
              ))}
            </div>
            {/* Se vuelve a montar al cambiar los filtros: si ApexCharts conserva la
                barra elegida con otra cantidad de barras, el gráfico se rompe. */}
            <ReactApexChart
              key={[granularity, period, destinations.join(','), vias.join(','), colorBy, label, basis].join('|')}
              type='bar'
              height={320}
              options={chartOptions}
              series={bars.series.map((s) => ({ name: groupLabel(s.name), data: s.data.map(toUnit) }))}
            />
            <p className='flex items-center gap-1 text-xs text-default-500'>
              <Icon icon='tabler:hand-click' width={14} />
              {t('Selecciona una fecha para ver su detalle.')}
            </p>
          </>
        ) : (
          <EmptyState title={t('No hay naves con estos filtros.')} />
        )}

        {activeBucket && detailTitle && (
          <div className='flex flex-col gap-2'>
            <span className='text-sm font-semibold'>
              {detailTitle.title} · {periodLabel}
              <span className='font-normal text-default-500'>
                {' · '}
                {detailTitle.count} · {v(detailTitle.kilos)} {u}
              </span>
            </span>
            {dimension ? (
              <>
                {detail == null ? (
                  <LoadingState label={t('Cargando…')} />
                ) : (
                  <div className='overflow-x-auto'>
                    <Table aria-label={t(DETAIL_TITLES[dimension].title)} removeWrapper isStriped isCompact>
                      <TableHeader>
                        <TableColumn>{t(DETAIL_TITLES[dimension].column)}</TableColumn>
                        <TableColumn className='text-right'>{t(header)}</TableColumn>
                        <TableColumn className='text-right'>{t('% DE LA BARRA')}</TableColumn>
                        <TableColumn className='text-right'>{t('TÚ')}</TableColumn>
                      </TableHeader>
                      <TableBody emptyContent={<EmptyState title={t('Sin datos para esta fecha.')} />}>
                        {detailShown.map(([key, name, kilos, mine]) => {
                          const isMine = dimension === 'exporter' && mine > 0;
                          return (
                            <TableRow key={key} className={isMine ? 'bg-primary-50 dark:bg-primary-50/10' : undefined}>
                              <TableCell className='font-medium'>
                                {dimension === 'exporter' && !isMine ? displayName(name).toLocaleUpperCase('es') : name}
                                {isMine && (
                                  <Chip size='sm' variant='flat' className='ml-2'>
                                    {t('tú')}
                                  </Chip>
                                )}
                              </TableCell>
                              <TableCell className='text-right'>{v(kilos)}</TableCell>
                              <TableCell className='text-right'>{pct(detailTotal ? kilos / detailTotal : null)}</TableCell>
                              <TableCell className='text-right'>{mine > 0 ? v(mine) : '—'}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
                {(detail?.length ?? 0) > VOYAGES_SHOWN && (
                  <Button size='sm' variant='light' className='w-fit' onPress={() => setShowAllVoyages(!showAllVoyages)}>
                    {showAllVoyages
                      ? t('Ver solo los {{count}} más grandes', { count: VOYAGES_SHOWN })
                      : t('Ver todos ({{count}})', { count: detail!.length })}
                  </Button>
                )}
              </>
            ) : (
            <>
            <div className='overflow-x-auto'>
              <Table aria-label={t('Naves')} removeWrapper isStriped isCompact>
                <TableHeader>
                  <TableColumn>{t('NAVE')}</TableColumn>
                  <TableColumn>{t('PUERTO DE DESCARGA')}</TableColumn>
                  <TableColumn>{t('ZARPE')}</TableColumn>
                  <TableColumn>{t('ARRIBO')}</TableColumn>
                  <TableColumn>{t('VARIEDADES A BORDO')}</TableColumn>
                  <TableColumn className='text-right'>{t(header)}</TableColumn>
                  <TableColumn className='text-right'>{t('EXPORTADORAS')}</TableColumn>
                  <TableColumn className='text-right'>{t('TÚ')}</TableColumn>
                </TableHeader>
                <TableBody emptyContent={<EmptyState title={t('Sin naves.')} />}>
                  {(showAllVoyages ? voyages : voyages.slice(0, VOYAGES_SHOWN)).map((voyage) => (
                    <TableRow key={`${voyage.date}|${voyage.ship}|${voyage.port}`}>
                      {/* Aéreo y terrestre vienen agrupados por vía: la "nave" es AÉREO o TERRESTRE. */}
                      <TableCell className='font-medium'>{voyage.ship}</TableCell>
                      <TableCell>
                        {voyage.port}
                        <span className='text-xs text-default-400'> · {voyage.country}</span>
                      </TableCell>
                      <TableCell className='whitespace-nowrap'>{day(voyage.etd)}</TableCell>
                      <TableCell className='whitespace-nowrap'>{day(voyage.eta)}</TableCell>
                      <TableCell className='text-xs'>
                        {voyage.varieties.slice(0, 3).join(', ')}
                        {voyage.varieties.length > 3 && (
                          <span className='text-default-400'> +{voyage.varieties.length - 3}</span>
                        )}
                      </TableCell>
                      <TableCell className='text-right'>{v(voyage.kilos)}</TableCell>
                      <TableCell className='text-right'>{voyage.exporters}</TableCell>
                      <TableCell className='text-right'>{voyage.mine > 0 ? v(voyage.mine) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {voyages.length > VOYAGES_SHOWN && (
              <Button size='sm' variant='light' className='w-fit' onPress={() => setShowAllVoyages(!showAllVoyages)}>
                {showAllVoyages
                  ? t('Ver solo las {{count}} más cargadas', { count: VOYAGES_SHOWN })
                  : t('Ver las {{count}} naves', { count: voyages.length })}
              </Button>
            )}
            </>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
